import React, { useState } from 'react';
import {
  Box, Paper, Typography, Accordion, AccordionSummary, AccordionDetails, Avatar,
  Table, TableBody, TableCell, TableHead, TableRow, Chip, Button, CircularProgress, Stack,
  IconButton, Tooltip,
} from '@mui/material';
import {
  ExpandMore, AccountTree as MapIcon, MenuBook as CatalogIcon, History as HistoryIcon,
  Download as DownloadIcon, Refresh as RefreshIcon, DeleteOutline as DeleteIcon,
} from '@mui/icons-material';
import axios from 'axios';
import config from '../../../config';
import { toast } from 'react-toastify';

const GRADIENT = 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)';

interface Mapping { accountCode: string; accountName: string; accountNameAr: string; elementName: string; elementLabelAr: string; statement: string; sign: number; classification?: string; eliminationRule?: string; }
interface Element { elementId: number; elementName: string; labelAr?: string; labelEn?: string; balance?: string; periodType?: string; statement?: string; }
interface Run { runId: number; fromDate?: string; toDate?: string; entityIdentifier?: string; status?: string; generatedAt?: string; packagePath?: string; }

const XbrlReviewPanel: React.FC<{ isRTL: boolean }> = ({ isRTL }) => {
  const [mappings, setMappings] = useState<Mapping[] | null>(null);
  const [elements, setElements] = useState<Element[] | null>(null);
  const [runs, setRuns] = useState<Run[] | null>(null);
  const [loading, setLoading] = useState<string>('');

  const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });

  const load = async (kind: 'mappings' | 'elements' | 'runs') => {
    setLoading(kind);
    try {
      const r = await axios.get(`${config.API_BASE_URL}/xbrl/${kind}`, auth());
      if (kind === 'mappings') setMappings(r.data);
      if (kind === 'elements') setElements(r.data);
      if (kind === 'runs') setRuns(r.data);
    } catch {
      toast.error(isRTL ? 'فشل التحميل' : 'Load failed');
    } finally {
      setLoading('');
    }
  };

  const downloadPackage = async (runId: number) => {
    try {
      const r = await axios.get(`${config.API_BASE_URL}/xbrl/runs/${runId}/package`, { ...auth(), responseType: 'blob' });
      const a = document.createElement('a');
      a.href = URL.createObjectURL(r.data);
      a.download = `xbrl_run_${runId}.zip`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error(isRTL ? 'الحزمة غير متوفرة' : 'Package not found');
    }
  };

  const deleteRun = async (runId: number) => {
    if (!window.confirm(isRTL ? `حذف عملية التوليد رقم ${runId}؟` : `Delete run #${runId}?`)) return;
    try {
      await axios.delete(`${config.API_BASE_URL}/xbrl/runs/${runId}`, auth());
      setRuns((prev) => (prev ? prev.filter((x) => x.runId !== runId) : prev));
      toast.success(isRTL ? 'تم الحذف' : 'Deleted');
    } catch {
      toast.error(isRTL ? 'فشل الحذف' : 'Delete failed');
    }
  };

  const accHeader = (icon: React.ReactNode, title: string, count?: number) => (
    <AccordionSummary expandIcon={<ExpandMore />}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
        <Avatar variant="rounded" sx={{ width: 34, height: 34, background: GRADIENT }}>{icon}</Avatar>
        <Typography fontWeight={700}>{title}</Typography>
        {count !== undefined && <Chip size="small" label={count} sx={{ fontWeight: 700 }} />}
      </Box>
    </AccordionSummary>
  );

  const accSx = { borderRadius: 3, mb: 1.5, '&:before': { display: 'none' }, border: '1px solid', borderColor: 'divider', boxShadow: '0 2px 12px rgba(0,0,0,0.05)' };

  return (
    <Paper elevation={0} sx={{ mt: 3, p: { xs: 1.5, sm: 2.5 }, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.08)', border: '1px solid', borderColor: 'divider' }} dir={isRTL ? 'rtl' : 'ltr'}>
      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
        {isRTL ? 'مراجعة الوسم (للتدقيق)' : 'XBRL Review (audit)'}
      </Typography>

      {/* Mappings */}
      <Accordion disableGutters sx={accSx} onChange={(_e, ex) => ex && !mappings && load('mappings')}>
        {accHeader(<MapIcon sx={{ color: '#fff', fontSize: 20 }} />, isRTL ? 'جدول الربط (حساب ← عنصر)' : 'Account → Element mapping', mappings?.length)}
        <AccordionDetails>
          {loading === 'mappings' ? <CircularProgress size={26} /> : mappings && (
            <Box sx={{ overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { background: GRADIENT, color: '#fff !important', fontWeight: 700, whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'رمز الحساب' : 'Account'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'اسم الحساب' : 'Name'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'العنصر' : 'Element'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'القائمة' : 'Stmt'}</TableCell>
                    <TableCell align="center" sx={{ color: '#fff !important' }}>{isRTL ? 'الإشارة' : 'Sign'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {mappings.map((m, i) => (
                    <TableRow key={i} hover>
                      <TableCell sx={{ direction: 'ltr', fontFamily: 'monospace' }}>{m.accountCode}</TableCell>
                      <TableCell>{isRTL ? (m.accountNameAr || m.accountName) : m.accountName}</TableCell>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{isRTL ? (m.elementLabelAr || m.elementName) : m.elementName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr' }}>{m.elementName}</Typography>
                        {m.eliminationRule && <Chip size="small" color="warning" label={m.eliminationRule} sx={{ ml: 0.5, height: 18 }} />}
                      </TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={m.statement} color={m.statement === 'PL' ? 'warning' : 'primary'} sx={{ height: 22 }} /></TableCell>
                      <TableCell align="center">
                        <Chip size="small" label={m.sign} color={m.sign === 1 ? 'success' : m.sign === -1 ? 'error' : 'default'} sx={{ height: 22, minWidth: 34 }} />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Element catalog */}
      <Accordion disableGutters sx={accSx} onChange={(_e, ex) => ex && !elements && load('elements')}>
        {accHeader(<CatalogIcon sx={{ color: '#fff', fontSize: 20 }} />, isRTL ? 'كتالوج العناصر (IFRS)' : 'Element catalog (IFRS)', elements?.length)}
        <AccordionDetails>
          {loading === 'elements' ? <CircularProgress size={26} /> : elements && (
            <Box sx={{ overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { background: GRADIENT, color: '#fff !important', fontWeight: 700 } }}>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'العنصر' : 'Element'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'القائمة' : 'Stmt'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'الطبيعة' : 'Balance'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'النوع' : 'Period'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {elements.map((e, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={600}>{isRTL ? (e.labelAr || e.elementName) : e.elementName}</Typography>
                        <Typography variant="caption" color="text.secondary" sx={{ direction: 'ltr' }}>{e.elementName}</Typography>
                      </TableCell>
                      <TableCell><Chip size="small" variant="outlined" label={e.statement} color={e.statement === 'PL' ? 'warning' : 'primary'} sx={{ height: 22 }} /></TableCell>
                      <TableCell>{e.balance}</TableCell>
                      <TableCell>{e.periodType}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </AccordionDetails>
      </Accordion>

      {/* Run history */}
      <Accordion disableGutters sx={accSx} onChange={(_e, ex) => ex && !runs && load('runs')}>
        {accHeader(<HistoryIcon sx={{ color: '#fff', fontSize: 20 }} />, isRTL ? 'سجل عمليات التوليد' : 'Generation history', runs?.length)}
        <AccordionDetails>
          <Stack direction="row" sx={{ mb: 1 }}>
            <Button size="small" startIcon={<RefreshIcon />} onClick={() => load('runs')} sx={{ textTransform: 'none' }}>
              {isRTL ? 'تحديث' : 'Refresh'}
            </Button>
          </Stack>
          {loading === 'runs' ? <CircularProgress size={26} /> : runs && (runs.length === 0 ? (
            <Typography variant="body2" color="text.secondary">{isRTL ? 'لا توجد عمليات محفوظة بعد.' : 'No saved runs yet.'}</Typography>
          ) : (
            <Box sx={{ overflowX: 'auto', borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
              <Table size="small" sx={{ '& td, & th': { borderColor: 'divider' } }}>
                <TableHead>
                  <TableRow sx={{ '& th': { background: GRADIENT, color: '#fff !important', fontWeight: 700, whiteSpace: 'nowrap' } }}>
                    <TableCell sx={{ color: '#fff !important' }}>#</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'الفترة' : 'Period'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'السجل' : 'CR'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'الحالة' : 'Status'}</TableCell>
                    <TableCell sx={{ color: '#fff !important' }}>{isRTL ? 'التاريخ' : 'Date'}</TableCell>
                    <TableCell align="center" sx={{ color: '#fff !important' }}>{isRTL ? 'إجراءات' : 'Actions'}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {runs.map((r) => (
                    <TableRow key={r.runId} hover>
                      <TableCell>{r.runId}</TableCell>
                      <TableCell sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{r.fromDate?.substring(0, 10)} → {r.toDate?.substring(0, 10)}</TableCell>
                      <TableCell sx={{ direction: 'ltr' }}>{r.entityIdentifier || '—'}</TableCell>
                      <TableCell>
                        <Chip size="small" label={r.status} color={r.status === 'Generated' ? 'success' : 'warning'} variant="outlined" sx={{ height: 22 }} />
                      </TableCell>
                      <TableCell sx={{ direction: 'ltr', whiteSpace: 'nowrap' }}>{r.generatedAt?.substring(0, 16).replace('T', ' ')}</TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Button size="small" startIcon={<DownloadIcon />} onClick={() => downloadPackage(r.runId)} sx={{ textTransform: 'none' }}>zip</Button>
                          <Tooltip title={isRTL ? 'حذف' : 'Delete'}>
                            <IconButton size="small" color="error" onClick={() => deleteRun(r.runId)}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          ))}
        </AccordionDetails>
      </Accordion>
    </Paper>
  );
};

export default XbrlReviewPanel;
